/*
 * THE CALMER SIDEBAR (Oct 2026 redesign), its rules in one place.
 *
 * Gordon approved the proposal (todo-ui-proposal.md): six fixed entries -
 * Inbox, Today, Upcoming, Assigned, Notes and More - then Favourites (only
 * what the person pins with a star) and My lists. Everything else is one
 * click deeper, under More, and every one of them is in Ctrl K.
 *
 * Nothing here talks to the server. Favourites ride on the store the old
 * "Customise views" used (GET/PUT /todos/view-prefs, [{ key, hidden }],
 * keys /^[a-z][a-z0-9_]{0,23}$/, at most 40), so Phase 1 needs no backend
 * change:
 *
 *   NEW FORMAT   the first entry is { key: 'fav_v2', hidden: true }, and every
 *                entry after it with hidden: false is a favourite, in order.
 *   NOTHING YET  [] - nobody ever customised: Favourites holds only the
 *                saved filters they pinned (those keep their own flag).
 *   OLD FORMAT   anything else is a Customise-views order from before: the
 *                views they kept SHOWING that are not among the six fixed
 *                entries become their Favourites, so nobody loses what they
 *                chose. Read that way every time until they next pin or
 *                unpin, which writes the new format.
 *
 * A LABEL is pinned by a key made from its id: 'lb_' and the first and last
 * ten hex digits of its uuid (23 characters, inside the server's 24).
 */

export const FAV_MARK = 'fav_v2';
const MAX_PREFS = 40;

/** The views under More, in the order More shows them. */
export const MORE_VIEWS = ['all', 'flagged', 'p1', 'waiting', 'overdue', 'scheduled', 'nodate', 'done'];
/** More's pages: the Filters & labels page, Templates (staff) and the team's workload (staff). */
export const MORE_PAGES = ['filters', 'templates', 'workload'];
/** Assigned is one page; these are its tabs - To me, By me, Team. */
export const ASSIGNED_TABS = ['assigned', 'delegated', 'workload'];

/** What an old Customise-views order may carry over as a favourite. */
const CARRIED = new Set([...MORE_VIEWS, 'workload']);

export const labelKey = (id) => {
  const hex = String(id || '').toLowerCase().replace(/[^0-9a-f]/g, '');
  return `lb_${hex.length > 20 ? hex.slice(0, 10) + hex.slice(-10) : hex}`;
};
export const isLabelKey = (k) => /^lb_[0-9a-f]{1,20}$/.test(String(k || ''));
const pinnableKey = (k) => CARRIED.has(k) || MORE_PAGES.includes(k) || isLabelKey(k);

/** The favourites (keys, in order) the stored prefs say - see the top of this file. */
export function favouritesFrom(prefs) {
  if (!Array.isArray(prefs) || !prefs.length) return [];
  if (prefs[0] && prefs[0].key === FAV_MARK) {
    return prefs.slice(1).filter((p) => p && !p.hidden && pinnableKey(p.key)).map((p) => p.key);
  }
  return prefs.filter((p) => p && !p.hidden && CARRIED.has(p.key)).map((p) => p.key);
}

/** The prefs to store for these favourites. */
export const prefsFor = (keys) => [{ key: FAV_MARK, hidden: true },
  ...[...new Set(keys)].filter(pinnableKey).slice(0, MAX_PREFS - 1).map((key) => ({ key, hidden: false }))];

/*
 * KEYS FROM BEFORE. A stored selection (tda-selected:<who>), a link or a
 * test may still name a view that is no longer a destination:
 *   calendar   is Upcoming in the Calendar layout
 *   assigned, delegated, workload stay as they are - Assigned's three tabs.
 * Returns the selection to use and, for calendar, the layout to set.
 */
export function legacySel(sel) {
  if (sel && sel.kind === 'view' && sel.key === 'calendar') {
    return { sel: { kind: 'view', key: 'upcoming' }, layout: { key: 'upcoming', value: 'calendar' } };
  }
  return { sel, layout: null };
}

/* ------------------------------------------------ kept in this browser */

const read = (key, fallback) => {
  try {
    const v = JSON.parse(localStorage.getItem(key) || 'null');
    return v === null || v === undefined ? fallback : v;
  } catch (err) {
    return fallback;
  }
};
const write = (key, v) => {
  try { localStorage.setItem(key, JSON.stringify(v)); } catch (err) { /* not kept */ }
};

/*
 * THE LAYOUT of a page (List / Board / Calendar): Today and Upcoming, and
 * Calendar for a list, a label, a filter or a smart view, per person; List
 * or Board for a list stays the list's own (list.view, 060).
 *
 * FOLLOWS THE PERSON (26 Oct): kept on the server with the sidebar's views
 * (GET/PUT /todos/view-prefs `layouts` and `display`), so a page set to
 * Calendar on the desk is a Calendar on the phone. This browser's copy is
 * the offline cache - read first so the page draws at once, replaced by the
 * server's when it answers - and the first time a browser meets a server
 * that keeps them, what it had is sent up (TodoApp, 'prefs-up').
 */
const layoutKey = (who) => `tda-layout:${who || ''}`;
export const readLayouts = (who) => {
  const v = read(layoutKey(who), {});
  return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
};
export const writeLayouts = (who, v) => write(layoutKey(who), v);

/** The last places visited, newest first, for the palette's Recent. */
const recentKey = (who) => `tda-recent:${who || ''}`;
export const readRecent = (who) => {
  const v = read(recentKey(who), []);
  return Array.isArray(v) ? v.filter((x) => x && typeof x === 'object' && x.kind) : [];
};
export const pushRecent = (who, sel) => {
  if (!sel || !sel.kind) return;
  const same = (a) => a.kind === sel.kind && (a.key || null) === (sel.key || null) && (a.id || null) === (sel.id || null);
  write(recentKey(who), [sel, ...readRecent(who).filter((a) => !same(a))].slice(0, 5));
};

/** Whether More is open in the sidebar. */
const moreKey = (who) => `tda-more-open:${who || ''}`;
export const readMoreOpen = (who) => read(moreKey(who), false) === true;
export const writeMoreOpen = (who, on) => write(moreKey(who), Boolean(on));

/*
 * THE DISPLAY MENU's choices: per page (`tda-display:<who>`, keyed by the
 * page - 'today', 'list:<id>' ...) Group by, Sort and Show completed; per
 * person the density and whether Today shows its planned time.
 */
const displayKey = (who) => `tda-display:${who || ''}`;
export const readDisplay = (who) => {
  const v = read(displayKey(who), {});
  return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
};
export const writeDisplay = (who, v) => write(displayKey(who), v);

/*
 * The shapes the server keeps (lib/todo-smart.js on the server, which
 * refuses anything else with a 400 - so nothing it would refuse is sent):
 *   layouts  { page: 'board' | 'calendar' }        ('list' is the default)
 *   display  { density, planned, pages: { page: { group, sort, done } } }
 * This browser keeps the display flat - { density, planned, [page]: {...} } -
 * as it always has, so these translate.
 */
const PAGE_RE = /^(?:(?:list|label|filter|client):[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[a-z][a-z0-9_]{0,23})$/;
const MAX_PAGES = 100;
const isObj = (v) => Boolean(v) && typeof v === 'object' && !Array.isArray(v);

export function layoutsToServer(local) {
  const out = {};
  for (const [k, v] of Object.entries(isObj(local) ? local : {})) {
    if (Object.keys(out).length >= MAX_PAGES) break;
    if (PAGE_RE.test(k) && (v === 'board' || v === 'calendar')) out[k] = v;
  }
  return out;
}

export function displayToServer(local) {
  const d = isObj(local) ? local : {};
  const out = {};
  if (d.density === 'compact' || d.density === 'comfortable') out.density = d.density;
  if (typeof d.planned === 'boolean') out.planned = d.planned;
  const pages = {};
  for (const [k, v] of Object.entries(d)) {
    if (Object.keys(pages).length >= MAX_PAGES) break;
    if (!PAGE_RE.test(k) || !isObj(v) || ['density', 'planned', 'panel'].includes(k)) continue;
    const one = {};
    if (GROUPS.some((g) => g.key === v.group) && v.group !== 'default') one.group = v.group;
    if (SORTS.some((g) => g.key === v.sort) && v.sort !== 'default') one.sort = v.sort;
    if (v.done === true) one.done = true;
    if (Object.keys(one).length) pages[k] = one;
  }
  out.pages = pages;
  return out;
}

export function displayFromServer(server) {
  const s = isObj(server) ? server : {};
  const out = {};
  if (s.density) out.density = s.density;
  if (typeof s.planned === 'boolean') out.planned = s.planned;
  for (const [k, v] of Object.entries(isObj(s.pages) ? s.pages : {})) if (isObj(v)) out[k] = { ...v };
  return out;
}

/** Is there anything in these (either shape) worth keeping? */
export const anyPrefs = (layouts, display) => Object.keys(layoutsToServer(layouts)).length > 0
  || Object.keys(displayToServer(display)).some((k) => k !== 'pages')
  || Object.keys(displayToServer(display).pages).length > 0;

/** One-time things: the "things moved" tip, the first-run tips, the Enter notice. */
const onceKey = (who, what) => `tda-once:${what}:${who || ''}`;
export const readOnce = (who, what) => read(onceKey(who, what), null);
export const writeOnce = (who, what, v = true) => write(onceKey(who, what), v);

/* ------------------------------------------------- grouping and sorting */

export const GROUPS = [
  { key: 'default', label: 'Default' },
  { key: 'none', label: 'None' },
  { key: 'list', label: 'List' },
  { key: 'date', label: 'Date' },
  { key: 'priority', label: 'Priority' }
];
export const SORTS = [
  { key: 'default', label: 'Default' },
  { key: 'date', label: 'Date' },
  { key: 'priority', label: 'Priority' },
  { key: 'title', label: 'Name' },
  { key: 'added', label: 'Newest first' }
];

const dayOf = (t) => (t.due_date ? String(t.due_date).slice(0, 10) : null);
const prio = (t) => Number(t.priority) || 4;
const SORT_FN = {
  date: (a, b) => ((dayOf(a) || '9999') < (dayOf(b) || '9999') ? -1 : (dayOf(a) || '9999') > (dayOf(b) || '9999') ? 1
    : String(a.due_time || '99').localeCompare(String(b.due_time || '99'))),
  priority: (a, b) => prio(a) - prio(b),
  title: (a, b) => String(a.title || '').localeCompare(String(b.title || ''), 'en', { sensitivity: 'base' }),
  added: (a, b) => String(b.created_at || '').localeCompare(String(a.created_at || ''))
};

/**
 * A flat view's groups ([{ key, label, items }]) regrouped and resorted as
 * the Display menu says. 'default' leaves the view's own. Pinned to-dos
 * still float to the top of whatever group they land in.
 */
export function arrange(groups, { group = 'default', sort = 'default', listName = () => '', longDay = (d) => d,
                                  todayIso = '' } = {}) {
  if ((group === 'default' && sort === 'default') || !Array.isArray(groups)) return groups;
  const pinFirst = (a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0);
  const sorter = SORT_FN[sort] || null;
  const order = (items) => (sorter ? items.slice().sort((a, b) => pinFirst(a, b) || sorter(a, b)) : items);
  if (group === 'default') return groups.map((g) => ({ ...g, items: order(g.items) }));
  const all = groups.flatMap((g) => g.items);
  if (group === 'none') return all.length ? [{ key: 'all-flat', label: null, items: order(all) }] : [];
  const by = new Map();
  const put = (k, label, t) => {
    if (!by.has(k)) by.set(k, { key: `by-${k}`, label, items: [] });
    by.get(k).items.push(t);
  };
  for (const t of all) {
    if (group === 'list') put(`l${t.list_id || ''}`, listName(t.list_id) || 'No list', t);
    else if (group === 'priority') put(`p${prio(t)}`, prio(t) < 4 ? `Priority ${prio(t)}` : 'No priority', t);
    else {
      const d = dayOf(t);
      put(d ? `d${d}` : 'dnone', !d ? 'No date' : d < todayIso ? 'Overdue' : longDay(d), t);
    }
  }
  const keys = [...by.keys()];
  if (group === 'priority') keys.sort();
  if (group === 'date') {
    // Overdue first (all of it under one heading), then by day, No date last.
    const late = keys.filter((k) => k !== 'dnone' && k.slice(1) < todayIso);
    if (late.length > 1) {
      const merged = late.flatMap((k) => by.get(k).items);
      late.forEach((k) => by.delete(k));
      by.set('dlate', { key: 'by-dlate', label: 'Overdue', items: merged });
    }
    return ['dlate', ...[...by.keys()].filter((k) => k !== 'dlate' && k !== 'dnone').sort(), 'dnone']
      .filter((k) => by.has(k)).map((k) => ({ ...by.get(k), items: order(by.get(k).items) }));
  }
  return keys.map((k) => ({ ...by.get(k), items: order(by.get(k).items) }));
}

/* ------------------------------------------------------ the palette's search */

/**
 * A small fuzzy score: every typed character in order, closer together and
 * at word starts scoring higher; 0 when it does not match at all.
 */
export function fuzzy(text, q) {
  const s = String(text || '').toLowerCase();
  const t = String(q || '').trim().toLowerCase();
  if (!t) return 1;
  const at = s.indexOf(t);
  if (at >= 0) return 1000 - at + (at === 0 || /\W/.test(s[at - 1] || ' ') ? 200 : 0);
  let score = 0;
  let i = 0;
  let last = -1;
  for (const ch of t) {
    if (ch === ' ') continue;
    const j = s.indexOf(ch, i);
    if (j < 0) return 0;
    score += j === last + 1 ? 6 : 1;
    if (j === 0 || /\W/.test(s[j - 1])) score += 4;
    last = j;
    i = j + 1;
  }
  return score;
}
